import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {serverAlertRuleAPI} from 'features/server/api/serverAlertRuleAPI';
import {groupServerAlertRules} from 'features/server/lib/serverAlertRuleHelper';
import {userSelector} from 'entities/user';

import IServerOutput from './Server/IServer';
import IServerAlertRule from './ServerAlertRule/IServerAlertRule';
import {IServerAlertRuleMetricOption} from './ServerAlertRule/IServerAlertRuleRecommendations';
import useCanCreateServerAlertRule from './useCanCreateServerAlertRule';

const RULES_LIMIT = 200;
const EMPTY_RULES: IServerAlertRule[] = [];
const EMPTY_METRIC_OPTIONS: IServerAlertRuleMetricOption[] = [];

export interface IUseServerAlertRules {
    items: IServerAlertRule[],
    firstCommonUuid: string | undefined,
    firstOwnUuid: string | undefined,
    enabledCount: number,
    metricOptions: IServerAlertRuleMetricOption[],
    rulesLimit: number,
    isLoading: boolean,
    canCreate: boolean,
}

export const useServerAlertRules = (server: IServerOutput): IUseServerAlertRules => {
    const user = useSelector(userSelector);
    const canCreate = useCanCreateServerAlertRule();

    const {data: pagination, isLoading} = serverAlertRuleAPI.useGetServerAlertRulesQuery({
        limit: RULES_LIMIT,
    });
    const {data: recommendations} = serverAlertRuleAPI.useGetServerAlertRuleRecommendationsQuery();

    const {items, firstCommonUuid, firstOwnUuid} = useMemo(
        () => groupServerAlertRules(pagination?.items ?? EMPTY_RULES, server.uuid),
        [pagination?.items, server.uuid],
    );
    const enabledCount = useMemo(() => items.filter((item) => item.enabled).length, [items]);

    return {
        items,
        firstCommonUuid,
        firstOwnUuid,
        enabledCount,
        metricOptions: recommendations?.metrics ?? EMPTY_METRIC_OPTIONS,
        rulesLimit: user?.tariff.server_alert_rules_max_count_limit ?? 0,
        isLoading,
        canCreate,
    };
};

export default useServerAlertRules;
